import { BigNumber } from '@xylabs/bignumber'
import { assertEx, EthAddress } from '@xylabs/sdk-js'
import { TypedEvent } from '@xyo-network/world-typechain'

import { XyoWorldBase } from '../../Base'
import { XyoWorldGeotokensWrapper, XyoWorldGeotokenWrapper } from '../../EthersClients'
import { BaseJsonObject } from '../../models'
import { Quadkey } from '../../Quadkey'
import { XyoErc721MetaData } from '../../wrappers'

export interface GeotokenJsonObject extends BaseJsonObject {
  blockNumber?: number
  quadkey: string
}

const mapboxToken = 'pk.eyJ1IjoieHlmaW5kYWJsZXMiLCJhIjoiY2ttc2M5amo2MGdmbjJ3bnhwbWEyNXBoeiJ9.vufydtBLmZOXBCQPnCLEsA'

export class XyoWorldGeotoken extends XyoWorldBase<GeotokenJsonObject> {
  private static xyoTokenAddress = assertEx(EthAddress.fromString('0x55296f69f40Ea6d20E478533C15A6B08B654E758'))

  public blockNumber?: number

  public geotoken: XyoWorldGeotokenWrapper

  public geotokens: XyoWorldGeotokensWrapper

  public quadkey: Quadkey

  private _balance?: BigNumber

  private _erc721MetaData?: XyoErc721MetaData

  private _locked?: boolean

  private _owner?: EthAddress | null

  constructor(quadkey: Quadkey, geotokens: XyoWorldGeotokensWrapper, jsonObject?: GeotokenJsonObject) {
    super()
    this.quadkey = quadkey
    this.geotokens = geotokens
    this.geotoken = XyoWorldGeotokenWrapper.get(this.geotokens, this.quadkey)
    this.loadJsonObject(jsonObject)
  }

  public async fetchBalance() {
    if (!(await this.fetchExists())) return null
    return (this._balance = new BigNumber((await this.geotokens.erc20Balance(this.quadkey, XyoWorldGeotoken.xyoTokenAddress)).toString()))
  }

  public async fetchErc721MetaData() {
    this._erc721MetaData = new XyoErc721MetaData(this.quadkey, true, mapboxToken)
    return await this._erc721MetaData?.load()
  }

  public async fetchExists() {
    return await this.geotokens.exists(this.quadkey)
  }

  public async fetchLocked() {
    if (!(await this.fetchExists())) return null
    return (this._locked = await this.geotokens.locked(this.quadkey))
  }

  public async fetchOwner() {
    if (!(await this.fetchExists())) return null
    return (this._owner = await this.geotokens.ownerOf(this.quadkey))
  }

  public async getBalance() {
    return this._balance ?? (await this.fetchBalance())
  }

  public async getErc721MetaData() {
    return this._erc721MetaData ?? (await this.fetchErc721MetaData())
  }

  public async getLocked() {
    return this._locked ?? (await this.fetchLocked())
  }

  public async getOwner() {
    return this._owner ?? (await this.fetchOwner())
  }

  public async load() {
    const promises: Promise<unknown>[] = [this.getOwner(), this.getLocked(), this.getBalance()]
    await Promise.all(promises)
  }

  public loadJsonObject(jsonObject?: GeotokenJsonObject) {
    if (jsonObject) {
      this.blockNumber = jsonObject.blockNumber
    }
    return this
  }

  public async refresh() {
    await this.load()
    return this
  }

  public toJsonObject(): GeotokenJsonObject {
    return {
      blockNumber: this.blockNumber,
      quadkey: this.quadkey.toBase4HashLabel(),
    }
  }

  public async update() {
    await this.load()
    return this
  }

  public async updateWithEvents(_events: TypedEvent[]) {
    await this.load()
    return this
  }

  private log(message: string) {
    return console.log(`Auction: ${message} [${this.quadkey.toBase4HashLabel()}]`)
  }

  private warn(message: string) {
    return console.warn(`Auction: ${message} [${this.quadkey.toBase4HashLabel()}]`)
  }
}
